import React from 'react';
import { ChevronUp, ChevronDown, Minus } from 'lucide-react';

/**
 * RoleHierarchyIndicator Component
 * 
 * Visual indicator showing role hierarchy level with comparative display
 * 
 * @param {Object} props
 * @param {number} props.level - Current hierarchy level
 * @param {number} props.maxLevel - Maximum hierarchy level (default: 100)
 * @param {string} props.size - Size variant: 'sm', 'md', 'lg'
 * @param {boolean} props.showLabel - Show text label
 * @param {boolean} props.showComparison - Show comparison to max level
 */
export default function RoleHierarchyIndicator({
  level = 50,
  maxLevel = 100,
  size = 'md',
  showLabel = true,
  showComparison = false
}) {
  // Calculate percentage
  const percentage = (level / maxLevel) * 100;

  // Get color based on level
  const getColor = () => {
    if (level >= 80) return 'text-purple-600 bg-purple-100';
    if (level >= 60) return 'text-blue-600 bg-blue-100';
    if (level >= 40) return 'text-green-600 bg-green-100';
    if (level >= 20) return 'text-yellow-600 bg-yellow-100';
    return 'text-gray-600 bg-gray-100';
  };

  // Size classes
  const sizeClasses = {
    sm: 'h-1.5 text-xs',
    md: 'h-2 text-sm',
    lg: 'h-3 text-base'
  };

  return (
    <div className="flex items-center gap-2">
      {/* Visual Bar */}
      <div className={`flex-1 bg-gray-200 rounded-full overflow-hidden ${sizeClasses?.[size]}`}>
        <div
          className={`h-full transition-all duration-300 ${getColor()?.split(' ')?.[1]}`}
          style={{ width: `${percentage}%` }}
        />
      </div>

      {/* Level Display */}
      {showLabel && (
        <div className={`flex items-center gap-1 ${getColor()?.split(' ')?.[0]} font-semibold`}>
          {level >= 80 ? (
            <ChevronUp className="w-4 h-4" />
          ) : level >= 40 ? (
            <Minus className="w-4 h-4" />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
          <span className={sizeClasses?.[size]?.split(' ')?.[1]}>
            L{level}
          </span>
        </div>
      )}

      {/* Comparison */}
      {showComparison && (
        <span className={`text-xs text-gray-500 ${sizeClasses?.[size]?.split(' ')?.[1]}`}>
          /{maxLevel}
        </span>
      )}
    </div>
  );
}